import { Formik, type FormikErrors } from "formik";
import { $enum } from "ts-enum-util";
import { type InferType, array, boolean, mixed, number, object, string } from "yup";

import { HttpRequestMethod, ParameterLocation, SpecVisibility, TargetPlatform } from "../../../../gql-generated/types";
import { onlyCameraKitSelected } from "../../../utils/onlyCameraKitSelected";

export const validTargetPlatforms = $enum(TargetPlatform)
    .getValues()
    .filter((value) => value !== TargetPlatform.TargetPlatformUnset);

export type ValidTargetPlatforms = (typeof validTargetPlatforms)[number];

export const validMethods = $enum(HttpRequestMethod)
    .getValues()
    .filter((value) => value !== HttpRequestMethod.MethodUnset);

export type ValidMethods = (typeof validMethods)[number];

export const validParameterLocations = $enum(ParameterLocation)
    .getValues()
    .filter((value) => value !== ParameterLocation.LocationUnset);

export type ValidParameterLocations = (typeof validParameterLocations)[number];

export const validVisibilities = [SpecVisibility.Private];

const parametersSchema = array().of(
    object().shape({
        location: mixed<ValidParameterLocations>().oneOf(validParameterLocations).required("Location is required"),
        name: string().required("Parameter name is required"),
        required: boolean(),
        constant: boolean(),

        defaultValue: string().when(["required", "constant"], ([required, constant]: boolean[], schema) => {
            if (!required && constant) {
                return schema.required("Default value is required");
            } else {
                return schema;
            }
        }),
    }),
);

export const remoteApiSpecSchema = object({
    id: string(),
    name: string().required("Name is required"),
    providerName: string().required("Provider Name is required"),
    documentationUrl: string().url("Documentation URL must be a valid URL"),
    iconUrl: string().url("Icon URL must be a valid URL"),
    description: string().required("Description is required"),
    targetPlatforms: array()
        .of(mixed<ValidTargetPlatforms>().oneOf(validTargetPlatforms).required())
        .required("Target Platform is required"),
    visibility: string().required("Visibility is required").oneOf(validVisibilities),
    minLensStudioVersion: string().matches(
        /^\d+(\.\d+)?(\.\d+)?(\.\d+)?$/,
        "Minimum Lens Studio version must be of format x.x.x.x",
    ),
    host: string()
        .matches(/^(([\da-z-]+\.)*([\da-z-]+))$/, "Valid host required")
        .when(
            ["targetPlatforms", "useBuiltinLocalProcessor"],
            ([targetPlatforms, useBuiltinLocalProcessor], schema) => {
                if (!onlyCameraKitSelected(targetPlatforms as TargetPlatform[]) || useBuiltinLocalProcessor) {
                    return schema.required("Host is required");
                }
                return schema;
            },
        ),

    endpoints: array()
        .of(
            object({
                refId: string().required("Ref ID is required"),
                path: string().required("Path is required"),
                methods: array()
                    .of(mixed<ValidMethods>().oneOf(validMethods).required("Method is required"))
                    .required(),

                parameters: parametersSchema,
            }),
        )
        .required(),

    customVisibilityUsersList: array().of(string().required()),

    maxRequestSizeBytes: number(),
    maxResponseSizeBytes: number(),
    maxResponseTimeMillis: number(),

    orgId: string(),
    snapkitAppId: string(),

    useBuiltinLocalProcessor: boolean(),
});

export type RemoteApiFormValues = InferType<typeof remoteApiSpecSchema>;
export type RemoteApiFormKeys = keyof RemoteApiFormValues;

export const EditRemoteApiFormik = Formik<RemoteApiFormValues>;

export const initialRemoteApiSpecValues: RemoteApiFormValues = {
    name: "",
    providerName: "",
    documentationUrl: "",
    iconUrl: "",
    description: "",
    targetPlatforms: [],
    visibility: SpecVisibility.Private,
    minLensStudioVersion: "",
    host: "",
    endpoints: [],
    customVisibilityUsersList: [],
    maxRequestSizeBytes: 1000,
    maxResponseSizeBytes: 1000,
    maxResponseTimeMillis: 3000,
    orgId: "",
    snapkitAppId: "",
    useBuiltinLocalProcessor: true,
};

export const initialEndpointValue = {
    refId: "",
    path: "",
    methods: [HttpRequestMethod.Get],
    parameters: [],
} as const;

export const initialParameterValue = {
    location: ParameterLocation.Query,
    name: "",
    required: false,
    constant: false,
    defaultValue: "",
} as const;

export const apisPageIsValid = (errors: FormikErrors<RemoteApiFormValues>): boolean => {
    const apiPageErrors = Object.keys(errors).filter((key) => key !== "endpoints");
    return apiPageErrors.length === 0;
};
